import { useApi, useToaster } from "@shopexperts/shared";
import { useMutation, useQueryClient } from "@tanstack/vue-query";

/**
 * Composable for submitting a draft lead and converting it to an actual lead (admin)
 * 
 * Admins can submit for existing users without requiring their authentication.
 * 
 * @example
 * ```ts
 * const { mutate, mutateAsync, isPending } = useSubmitDraftLead();
 * 
 * // Submit a draft lead
 * const result = await mutateAsync(123);
 * console.log(result.lead_id, result.is_new_account);
 * ```
 */
export function useSubmitDraftLead() {
    const api = useApi();
    const toaster = useToaster();
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
        mutationFn: (id: number) => {
            return api.admin.draftLeads.submitDraftLead({
                params: { id },
            });
        },
        onSuccess: (result, submittedId) => {
            queryClient.invalidateQueries({ queryKey: ["admin", "draft-leads"] });
            queryClient.invalidateQueries({ queryKey: ["admin", "draft-leads", submittedId] });
            // Also invalidate leads list since we created a new lead
            queryClient.invalidateQueries({ queryKey: ["admin", "leads"] });

            const accountMessage = result.is_new_account
                ? "An invitation was sent to the client."
                : "Lead assigned to existing account.";

            toaster.create({
                title: "Draft lead submitted successfully",
                description: accountMessage,
                type: "success",
            });
        },
        onError: (error) => {
            toaster.create({
                title: "Failed to submit draft lead",
                description: error.message || "An error occurred",
                type: "error",
            });
            console.error("[useSubmitDraftLead] Error:", error);
        },
    });

    return {
        mutate,
        mutateAsync,
        isPending,
        error,
        data,
        reset,
    };
}
